import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../../components/Dashboard/Header/Header';
import {
  Search,
  Plus,
  Pencil,
  MoreVertical,
  Trash2,
  Loader2,
  Calendar as CalendarIcon,
  ChevronRight,
  Download,
  X,
  FileText
} from 'lucide-react';
import api from '../../../api/axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import styles from './Holidays.module.css';
import HolidayDrawer from './HolidayDrawer';
import Pagination from '../../../components/Common/Pagination/Pagination';
import Breadcrumbs from '../../../components/Common/Breadcrumbs/Breadcrumbs';
import CustomSelect from '../../../components/Common/Select/CustomSelect';
import Footer from '../../../components/Common/Footer/Footer';
import NoData from '../../../components/Common/NoData/NoData';
import { useLoading } from '../../../context/LoadingContext';
import { 
  format, 
  addMonths, 
  subMonths, 
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek, 
  isSameMonth, 
  isSameDay, 
  addDays, 
  parseISO 
} from 'date-fns';
import { LayoutGrid, List } from 'lucide-react';

const MySwal = withReactContent(Swal);

const Holidays = () => {
  const navigate = useNavigate();
  const { loading, showLoader, hideLoader } = useLoading();
  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const userPermissions = storedUser.permissions || [];
  const isAdmin = (storedUser.role || '').toLowerCase() === 'admin';
  const canCreate = isAdmin || userPermissions.includes('create holidays');
  const canEdit = isAdmin || userPermissions.includes('edit holidays');
  const canDelete = isAdmin || userPermissions.includes('delete holidays');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [isCalendarView, setIsCalendarView] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    total: 5,
    per_page: 10
  });

  const fileInputRef = React.useRef(null);

  const handleImportClick = () => {
    setIsImportModalOpen(true);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // In a real app, you would send this to the backend
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const employee_id = user.id;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('employee_id', employee_id);

    
    try {
      await api.post('/holidays/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`${file.name} imported successfully!`);
      fetchHolidays();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to import holidays');
    } finally {
      
      e.target.value = ''; // Reset input
      setIsImportModalOpen(false);
    }
  };

  const handleDownloadSample = () => {
    const link = document.createElement('a');
    link.href = '/holiday_template.csv';
    link.download = 'holiday_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const fetchHolidays = async () => {
    
    try {
      const response = await api.get(`/holidays?page=${pagination.current_page}&per_page=${pagination.per_page}&search=${searchTerm}`);
      if (response.data) {
        setHolidays(response.data.data);
        setPagination({
          current_page: response.data.current_page,
          last_page: response.data.last_page,
          total: response.data.total,
          per_page: response.data.per_page
        });
      }
    } catch (error) {
      console.error('Error fetching holidays:', error);
      toast.error('Failed to fetch holidays');
    } finally {
      
    }
  };

  useEffect(() => {
    fetchHolidays();
  }, [pagination.current_page, pagination.per_page, searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.action-wrapper')) {
        setOpenMenuIndex(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const toggleActionMenu = (id, e) => {
    if (openMenuIndex === id) {
      setOpenMenuIndex(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      setMenuPosition({ top: rect.bottom + 4, left: rect.right - 160 });
      setOpenMenuIndex(id);
    }
  };

  const handleAddClick = () => {
    setEditingHoliday(null);
    setIsDrawerOpen(true);
  };

  const handleEditClick = (holiday) => {
    setEditingHoliday(holiday);
    setIsDrawerOpen(true);
  };

  const handleDelete = async (holiday) => {
    setOpenMenuIndex(null);
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const employee_id = user.id;

    MySwal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete the holiday "${holiday.holiday_title}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f97316',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.delete(`/holidays/${holiday.id}`, {
            data: { employee_id: employee_id }
          });
          toast.success('Holiday deleted successfully');
          fetchHolidays();
        } catch (error) {
          toast.error(error.response?.data?.message || 'Failed to delete holiday');
        }
      }
    });
  };

  const getTypeBadgeClass = (type) => {
    if (!type) return '';
    const t = type.toLowerCase();
    if (t.includes('public')) return styles['badge-public'];
    if (t.includes('company')) return styles['badge-company'];
    if (t.includes('week off') || t.includes('weekoff')) return styles['badge-weekoff'];
    return '';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const CalendarView = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const rows = [];
    let days = [];
    let day = startDate;
    let formattedDate = "";

    const onDateClick = (clickedDate) => {
      if (!canEdit) return;
      const holidaysOnDay = holidays.filter(h => isSameDay(parseISO(h.date), clickedDate));
      if (holidaysOnDay.length === 1) {
        handleEditClick(holidaysOnDay[0]);
      } else if (holidaysOnDay.length > 1) {
        // If multiple, maybe just show list or toggle first for now
        handleEditClick(holidaysOnDay[0]);
        toast.info(`This day has ${holidaysOnDay.length} holidays. Showing first one.`);
      } else {
        setEditingHoliday(null);
      }
    };

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        formattedDate = format(day, "d");
        const cloneDay = day;
        const holidaysOnDay = holidays.filter(h => isSameDay(parseISO(h.date), cloneDay));
        
        days.push(
          <div
            key={day}
            className={`${styles['calendar-day']} ${
              !isSameMonth(day, monthStart)
                ? styles['disabled']
                : isSameDay(day, new Date()) ? styles['today'] : ""
            } ${holidaysOnDay.length > 0 ? styles['has-holiday'] : ""}`}
            onClick={() => onDateClick(cloneDay)}
          >
            <span className={styles['day-number']}>{formattedDate}</span>
            <div className={styles['holiday-indicators-list']}>
              {holidaysOnDay.map((h, idx) => (
                <div key={idx} className={`${styles['holiday-indicator']} ${getTypeBadgeClass(h.holiday_type)}`}>
                  <span className={styles['holiday-dot']}></span>
                  <span className={styles['holiday-name']}>{h.holiday_title}</span>
                </div>
              ))}
            </div>
          </div>
        );
        day = addDays(day, 1);
      }
      rows.push(
        <div className={styles['calendar-row']} key={day}>
          {days}
        </div>
      );
      days = [];
    }

    return (
      <div className={styles['calendar-container']}>
        <div className={styles['calendar-header']}>
          <div className={styles['month-nav']}>
            <button onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} className={styles['nav-btn']}>
              <ChevronRight style={{ transform: 'rotate(180deg)' }} size={20} />
            </button>
            <h2>{format(currentMonth, "MMMM yyyy")}</h2>
            <button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} className={styles['nav-btn']}>
              <ChevronRight size={20} />
            </button>
          </div>
          <div className={styles['calendar-legend']}>
            <div className={styles['legend-item']}><span className={`${styles['dot']} ${styles['bg-public']}`}></span> Public Holiday</div>
            <div className={styles['legend-item']}><span className={`${styles['dot']} ${styles['bg-company']}`}></span> Company Holiday</div>
            <div className={styles['legend-item']}><span className={`${styles['dot']} ${styles['bg-weekoff']}`}></span> Week Off Day</div>
          </div>
        </div>
        <div className={styles['days-of-week']}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
            <div key={d} className={styles['week-name']}>{d}</div>
          ))}
        </div>
        <div className={styles['calendar-body']}>{rows}</div>
      </div>
    );
  };

  return (
    <div className={`${styles['holidays-layout']} ${isSidebarOpen ? styles['sidebar-open'] : ''}`}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <main className={styles['main-content']}>
          <Breadcrumbs customItems={[
            { label: 'Masters', path: '#' },
            { label: 'Holidays', path: '/holidays' }
          ]} />

          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <h1>Holidays</h1>
              <p>Manage public, company, and weekly holidays for the organization.</p>
            </div>
            <div className={styles['header-actions']}>
              <input 
                type="file" 
                ref={fileInputRef} 
                onChange={handleFileChange} 
                style={{ display: 'none' }} 
                accept=".csv, .xlsx, .xls"
              />
              {canCreate && (
                <>
                  <button className={styles['import-btn']} onClick={handleImportClick}>
                    <Download size={20} />
                    <span>Import</span>
                  </button>
                  <button className={styles['add-btn']} onClick={handleAddClick}>
                    <Plus size={20} />
                    <span>Add Holiday</span>
                  </button>
                </>
              )}
            </div>
          </div>

          <div className={styles['table-controls']}>
            <div className={styles['search-wrapper']}>
              <Search className={styles['search-icon']} size={18} />
              <input
                type="text"
                placeholder="Search holidays..."
                className={styles['search-input']}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className={styles['filter-actions']}>
              <div className={styles['view-toggle']}>
                <button 
                  className={`${styles['toggle-btn']} ${!isCalendarView ? styles['active'] : ''}`}
                  onClick={() => setIsCalendarView(false)}
                >
                  <List size={18} />
                </button>
                <button 
                  className={`${styles['toggle-btn']} ${isCalendarView ? styles['active'] : ''}`}
                  onClick={() => setIsCalendarView(true)}
                >
                  <LayoutGrid size={18} />
                </button>
              </div>
              <div className={styles['per-page-select-wrapper']}>
                <CustomSelect
                  isSearchable={false}
                  backgroundColor="white"
                  value={{ value: pagination.per_page, label: `${pagination.per_page} per page` }}
                  options={[
                    { value: 10, label: '10 per page' },
                    { value: 25, label: '25 per page' },
                    { value: 50, label: '50 per page' }
                  ]}
                  onChange={(opt) => setPagination(prev => ({ ...prev, per_page: opt.value }))}
                />
              </div>
            </div>
          </div>

          <div className={styles['data-view-container']}>
            {isCalendarView ? (
              <CalendarView />
            ) : (
              <>
                {/* Desktop Table */}
                <div className={styles['table-wrapper']}>
                  <table className={styles['holiday-table']}>
                    <thead>
                      <tr>
                        <th>DATE</th>
                        <th>HOLIDAY TITLE</th>
                        <th>TYPE</th>
                        <th className={styles['actions-cell']}>ACTIONS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {holidays.length > 0 ? (
                        holidays.map((holiday) => (
                          <tr key={holiday.id}>
                            <td className={styles['date-cell']}>
                              {formatDate(holiday.date)}
                            </td>
                            <td className={styles['title-cell']}>{holiday.holiday_title}</td>
                            <td>
                              <span className={`${styles['type-badge']} ${getTypeBadgeClass(holiday.holiday_type)}`}>
                                {holiday.holiday_type}
                              </span>
                            </td>
                            <td className={styles['actions-cell']}>
                              <div className={`action-wrapper ${styles['action-wrapper']}`}>
                                <button
                                  className={`${styles['action-btn']} ${openMenuIndex === holiday.id ? styles['active'] : ''}`}
                                  onClick={(e) => toggleActionMenu(holiday.id, e)}
                                >
                                  <MoreVertical size={18} />
                                </button>

                                {openMenuIndex === holiday.id && (
                                  <div
                                    className={styles['action-dropdown']}
                                    style={{ top: menuPosition.top, left: menuPosition.left }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {canEdit && (
                                      <button className={styles['dropdown-item']} onClick={() => {
                                        handleEditClick(holiday);
                                        setOpenMenuIndex(null);
                                      }}>
                                        <Pencil size={16} /> Edit
                                      </button>
                                    )}
                                    {canDelete && (
                                      <>
                                        <div className={styles['dropdown-divider']}></div>
                                        <button 
                                          className={`${styles['dropdown-item']} ${styles['deselect']}`}
                                          onClick={() => handleDelete(holiday)}
                                        >
                                          <Trash2 size={16} /> Delete
                                        </button>
                                      </>
                                    )}
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : !loading && (
                        <NoData message="No holidays found." colSpan={4} isTable={true} />
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Cards */}
                <div className={styles['holiday-cards']}>
                  {holidays.map((holiday) => (
                    <div key={holiday.id} className={styles['holiday-card']}>
                      <div className={styles['card-header']}>
                        <span className={styles['card-date']}>{formatDate(holiday.date)}</span>
                        <span className={`${styles['type-badge']} ${getTypeBadgeClass(holiday.holiday_type)}`}>
                          {holiday.holiday_type}
                        </span>
                      </div>
                      <div className={styles['card-title']}>{holiday.holiday_title}</div>
                      <div className={styles['card-footer']}>
                        {canEdit && (
                          <button className={styles['card-action-btn']} onClick={() => handleEditClick(holiday)}>
                            Edit
                          </button>
                        )}
                        {canDelete && (
                          <button className={`${styles['card-action-btn']} ${styles['delete']}`} onClick={() => handleDelete(holiday)}>
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {!isCalendarView && (
              <div className={styles['desktop-pagination']}>
                <Pagination
                  currentPage={pagination.current_page}
                  totalItems={pagination.total}
                  itemsPerPage={pagination.per_page}
                  onPageChange={(page) => setPagination(prev => ({ ...prev, current_page: page }))}
                />
              </div>
            )}
          </div>
          
          {!isCalendarView && (
            <div className={styles['mobile-pagination']}>
              <Pagination
                currentPage={pagination.current_page}
                totalItems={pagination.total}
                itemsPerPage={pagination.per_page}
                onPageChange={(page) => setPagination(prev => ({ ...prev, current_page: page }))}
              />
            </div>
          )}
        
          <Footer />
        </main>
      </div>

      <HolidayDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onSave={() => {
          setIsDrawerOpen(false);
          fetchHolidays();
        }}
        holiday={editingHoliday}
      />

      {/* Import Holidays Modal */}
      {isImportModalOpen && (
        <div className={styles['modal-overlay']} onClick={() => setIsImportModalOpen(false)}>
          <div className={styles['modal-content']} onClick={(e) => e.stopPropagation()}>
            <div className={styles['modal-header']}>
              <h3>Import Holidays</h3>
              <button className={styles['close-btn']} onClick={() => setIsImportModalOpen(false)}>
                <X size={20} />
              </button>
            </div>

            <div className={styles['modal-body']}>
              <div 
                className={styles['upload-dropzone']}
                onClick={() => fileInputRef.current?.click()}
              >
                <div className={styles['upload-icon-circle']}>
                  <FileText size={24} />
                </div>
                <h4>Drag and drop your .xlsx or .csv file here</h4>
                <p>or <span className={styles['browse-link']}>click to browse</span> your computer</p>
              </div>

              <button className={styles['template-download-link']} onClick={handleDownloadSample}>
                <Download size={16} />
                <span>Download Sample Template</span>
              </button>

              <div className={styles['info-alert']}>
                <div className={styles['info-icon-wrapper']}>
                  <Plus size={14} style={{ transform: 'rotate(45deg)', background: '#94a3b8', color: 'white', borderRadius: '50%' }} />
                </div>
                <p>Max file size 5MB. Supports up to 500 rows per import.</p>
              </div>
            </div>

            <div className={styles['modal-footer']}>
              <button className={styles['btn-cancel']} onClick={() => setIsImportModalOpen(false)}>
                Close
              </button>
              <button className={styles['btn-upload']} onClick={() => fileInputRef.current?.click()}>
                <Download size={18} />
                <span>Upload & Process</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Holidays;
